import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  adminPostbackQuotexInfo, adminGetSettings, adminUpdateSettings,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import {
  Copy, Check, Webhook, AlertTriangle, ExternalLink, ShieldCheck,
  DollarSign, MessageCircle, RefreshCcw, Save, Link as LinkIcon,
} from 'lucide-react';

const EVENT_LABELS = {
  registration: 'Registration (Quotex: reg)',
  email_confirmation: 'Email Confirmation (Quotex: conf)',
  ftd: 'FTD / First Deposit (Quotex: ftd)',
  deposit: 'Deposit / Redeposit (Quotex: dep)',
  withdrawal: 'Withdrawal (Quotex: withdrawal)',
};

const EVENT_DESC = {
  registration: 'Trader has registered.',
  email_confirmation: 'Trader confirmed their email.',
  ftd: 'Trader made their first deposit.',
  deposit: 'Trader made a repeat deposit.',
  withdrawal: 'Trader initiated a withdrawal.',
};

const EVENT_ICON = {
  registration: ShieldCheck,
  email_confirmation: MessageCircle,
  ftd: DollarSign,
  deposit: DollarSign,
  withdrawal: MessageCircle,
};

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={copy}
      className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
      data-testid={`qx-copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {done ? <Check className="h-3.5 w-3.5 text-[hsl(152_78%_55%)]" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function InfoTile({ label, value, hint, mono = false }) {
  return (
    <div className="rounded-md border border-border bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`mt-1 truncate text-xs ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminQuotexPostbackSetup() {
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([adminPostbackQuotexInfo(), adminGetSettings()]);
      setInfo(i);
      setSettings(s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Quotex info');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function saveSettings() {
    if (!settings || saving) return;
    setSaving(true);
    try {
      await adminUpdateSettings(settings);
      toast.success('Quotex settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }

  function up(k, v) { setSettings((s) => ({ ...s, [k]: v })); }

  if (loading) {
    return (
      <Card className="card-surface" data-testid="quotex-postback-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">Loading Quotex setup…</CardContent>
      </Card>
    );
  }
  if (!info || !settings) {
    return (
      <Card className="card-surface">
        <CardContent className="p-6 text-sm text-[hsl(0_72%_60%)]">Failed to load. Please refresh.</CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="quotex-postback-setup">
      <CardHeader className="border-b border-white/5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display uppercase tracking-wide text-base flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-[rgba(108,176,255,0.12)] border border-[rgba(108,176,255,0.32)] text-[10px] font-bold text-[hsl(212_80%_70%)]">QX</span>
              Quotex S2S Postback
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1.5">
              Track Quotex affiliate conversions (registration, FTD, deposit, withdrawal) via server-to-server postback.
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={load}
            className="h-8 gap-1 border-white/10 bg-white/[0.02] text-xs"
            data-testid="qx-refresh-btn">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-5">
        {/* Quotex affiliate link config */}
        <div className="rounded-lg border border-[rgba(108,176,255,0.20)] bg-[rgba(108,176,255,0.04)] p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-[hsl(212_80%_70%)]" />
              <div>
                <div className="text-sm font-semibold">Step 1 · Quotex affiliate link</div>
                <div className="text-[11px] text-muted-foreground">We append <code className="text-[hsl(212_80%_70%)]">cid=&#123;click_id&#125;</code> when redirecting users.</div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <span className="text-muted-foreground">Enable Quotex</span>
              <Switch checked={!!settings.quotex_enabled}
                onCheckedChange={(v) => up('quotex_enabled', v)}
                data-testid="qx-enabled-switch" />
            </label>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Quotex affiliate URL</Label>
            <Input
              value={settings.quotex_broker_link || ''}
              onChange={(e) => up('quotex_broker_link', e.target.value)}
              placeholder="https://broker.qxbroker.com/sign-up/?lid=YOUR_LID"
              maxLength={500}
              className="mt-1 bg-white/[0.02] border-white/10 text-xs font-mono"
              data-testid="qx-link-input"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Get this from your Quotex affiliate panel → Promo materials → Tracking link.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={saveSettings} disabled={saving}
              className="cta-sheen border-0 text-white font-semibold h-9 gap-1"
              data-testid="qx-save-btn">
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save'}
            </Button>
            {settings.quotex_broker_link && (
              <a
                href={info.redirect_url}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-[hsl(43_92%_56%)] hover:underline inline-flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" /> Test redirect
              </a>
            )}
          </div>
        </div>

        <Separator className="bg-white/5" />

        {/* Secret + helper info */}
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] flex items-center gap-1.5">
            <Webhook className="h-3.5 w-3.5" /> Step 2 · Postback URLs
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <InfoTile label="Postback secret" value={info.secret} mono
              hint="Auto-injected via &amp;secret=… in the URLs below. Don't share publicly." />
            <InfoTile label="Click param" value={info.click_param} mono
              hint="Quotex returns this as cid={click_id}." />
            <InfoTile label="Redirect URL"
              value={info.redirect_url}
              hint="Your tracking entry point. Use this in CTAs." mono />
          </div>
        </div>

        {/* Setup steps banner — Quotex's single-URL flow */}
        <div className="rounded-md border border-[rgba(108,176,255,0.32)] bg-[rgba(108,176,255,0.06)] p-4 space-y-3">
          <div className="flex items-start gap-2">
            <Webhook className="h-4 w-4 text-[hsl(212_80%_70%)] mt-0.5 shrink-0" />
            <div className="space-y-2 flex-1 min-w-0">
              <div className="font-semibold text-[hsl(212_80%_70%)] text-sm">Quotex setup — SINGLE URL flow</div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Quotex affiliate panel uses ONE postback URL + a "Fields" checkbox section.
                Paste the URL below into the URL field, then tick all field checkboxes — Quotex will fire
                the same URL for every event (reg / conf / ftd / dep / withdrawal) with the right macros.
              </p>
            </div>
          </div>

          {/* THE single URL */}
          <div className="rounded-md border border-[rgba(108,176,255,0.4)] bg-black/40 p-3 space-y-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[hsl(212_80%_70%)]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(212_80%_70%)]" />
              Postback URL · paste this into Quotex's "Postback URL" field
            </div>
            <div className="flex items-stretch gap-2">
              <code
                className="flex-1 min-w-0 text-[11px] font-mono break-all bg-black/40 border border-white/10 rounded px-2 py-2 leading-relaxed text-[hsl(212_80%_70%)]"
                data-testid="qx-single-url"
              >
                {info.single_postback_url}
              </code>
              <CopyButton text={info.single_postback_url} label="Postback URL" />
            </div>
          </div>

          {/* Fields checkbox guide */}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)]">
              ✅ Tick ALL these checkboxes in Quotex's "Fields" section:
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-[11px]">
              {Object.entries(info.field_checkboxes || {}).map(([k, v]) => (
                <div key={k} className="rounded border border-white/10 bg-white/[0.03] px-2 py-1.5 flex items-center gap-2">
                  <Check className="h-3 w-3 text-[hsl(152_78%_55%)] shrink-0" />
                  <code className="font-mono text-[hsl(212_80%_70%)] truncate">{k}</code>
                  <span className="text-muted-foreground">=</span>
                  <code className="font-mono text-muted-foreground truncate text-[10px]">{v}</code>
                </div>
              ))}
            </div>
          </div>

          <ol className="space-y-1 list-decimal pl-4 text-[11px] text-muted-foreground">
            <li>Open Quotex affiliate panel → <strong>Postback / S2S settings</strong>.</li>
            <li>Paste the <strong>single URL above</strong> into the Postback URL field.</li>
            <li>Tick <strong>ALL</strong> the field checkboxes shown above.</li>
            <li>Click <strong>Save Postback</strong>. Done — same URL handles all 5 events.</li>
            <li>Send users via <code className="text-[hsl(43_92%_56%)]">/api/broker/redirect/quotex</code> so we can attach a click_id; Quotex returns it back as <code>cid</code>.</li>
          </ol>
        </div>

        {/* Per-event URLs (reference) */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Reference · what Quotex will fire (per event)
          </div>
          {Object.entries(info.postback_urls || {}).map(([ev, url]) => {
            const Icon = EVENT_ICON[ev] || Webhook;
            return (
              <div key={ev} className="rounded-md border border-white/10 bg-white/[0.02] p-3.5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-8 w-8 rounded-md bg-[rgba(108,176,255,0.10)] border border-[rgba(108,176,255,0.30)] flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-[hsl(212_80%_70%)]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{EVENT_LABELS[ev] || ev}</div>
                      <div className="text-[11px] text-muted-foreground">{EVENT_DESC[ev] || ''}</div>
                    </div>
                  </div>
                  <Badge className="bg-[rgba(108,176,255,0.10)] text-[hsl(212_80%_70%)] border border-[rgba(108,176,255,0.32)] text-[10px] uppercase tracking-[0.18em] font-mono">
                    status={ev}
                  </Badge>
                </div>
                <div className="flex items-stretch gap-2">
                  <code
                    className="flex-1 min-w-0 text-[10px] font-mono break-all bg-black/30 border border-white/10 rounded px-2 py-2 leading-relaxed"
                    data-testid={`qx-url-${ev}`}
                  >
                    {url}
                  </code>
                  <CopyButton text={url} label={`URL ${ev}`} />
                </div>
              </div>
            );
          })}
        </div>

        <Separator className="bg-white/5" />

        <div className="text-[11px] text-muted-foreground leading-relaxed">
          <strong className="text-white">Quotex postback format reference:</strong>
          <br />
          <code className="text-[hsl(212_80%_70%)] font-mono break-all">
            ?status=&#123;status&#125;&amp;&#123;status&#125;=true&amp;eid=&#123;event_id&#125;&amp;cid=&#123;click_id&#125;&amp;sid=&#123;site_id&#125;&amp;lid=&#123;lid&#125;&amp;uid=&#123;trader_id&#125;&amp;country=&#123;country&#125;&amp;sumdep=&#123;sumdep&#125;&amp;sumwithdraw=&#123;sumwithdraw&#125;
          </code>
          <br />
          We map: <code>status</code> → event · <code>cid</code> → click_id · <code>uid</code> → trader_id · <code>sumdep/sumwithdraw</code> → amounts (USD).
        </div>
      </CardContent>
    </Card>
  );
}
